import { withSentry, captureRemixErrorBoundaryError } from '@sentry/remix'

import {
  data,
  isRouteErrorResponse,
  Outlet,
  useLocation,
  useRouteError,
  useRouteLoaderData,
} from '@remix-run/react'
import { QueryClientProvider } from '@tanstack/react-query'

import { Document } from '~/components/Document'
import { RootContext } from './hooks/useRootContext'
import tailwindCss from './tailwind.css?url'
import headerCss from './components/Header/header.css?url'

import type { ActionFunctionArgs, LoaderFunction, MetaFunction } from '@remix-run/node'
import { getPlans } from './queries/getPlans'
import { getCookieConsent, setCookie, getCookieConsentValue } from './cookie.server'

import TagManager from 'react-gtm-module'
import { useEffect, useState } from 'react'
import { getHeaderNav } from '~/queries/getHeaderNav'
import { getFooterNav } from './queries/getFooterNav'
import {
  preparePageContext,
  prepareRemixRequest,
  prepareRobotsMetadata,
} from './utils/routes/routeHelper'
import { Layout } from './components/Layout'
import { Colors } from './styles/colors'
import { ErrorComponent } from './components/error'
import ContentfulClientManager from './libs/contentful/ContentfulClientManager'
import { CIOAppContext } from './types/common_cio'
import UploadV2 from './components/Executives/Modal/UploadV2'
import { MainAppContextProvider } from './contexts/MainAppContext'
import { queryClient } from './libs/tanstack/client'

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const metaRobots = prepareRobotsMetadata(data.url)

  return [
    { name: 'robots', content: metaRobots ? metaRobots : 'noindex' },
    { title: 'Career.io – Changing Careers Worldwide' },
    {
      name: 'description',
      content:
        'Career platform for workforce individuals. For the job seeker who wants to maximize their success at landing your dream job, Career.io is an end-to-end career management platform that pulls together the best tools for landing the job of your dream.',
    },
  ]
}

export function links() {
  return [
    // fallback for older browsers
    {
      rel: 'icon',
      href: '/favicon/favicon.ico',
      sizes: 'any',
    },
    // primary favicon, SVG version
    {
      rel: 'icon',
      href: '/favicon/favicon.svg',
      type: 'image/svg+xml',
    },
    // Apple Home Screen icon
    { rel: 'apple-touch-icon', href: '/favicon/apple-touch-icon.png' },
    { rel: 'stylesheet', href: tailwindCss },
    { rel: 'stylesheet', href: headerCss },
  ]
}

export async function action({ request }: ActionFunctionArgs) {
  const reqBody = await request.formData()
  const cookieHeader = request.headers.get('Cookie')
  const cookieConsent = ['EssentialCookies', 'AnalyticalCookies', 'TargetingCookies']

  if (reqBody.get('analyticalCookies') !== '1') {
    cookieConsent.splice(cookieConsent.indexOf('AnalyticalCookies'), 1)
  } else if (reqBody.get('marketingAndTrackingCookies') !== '1') {
    cookieConsent.splice(cookieConsent.indexOf('TargetingCookies'), 1)
  }

  return data(
    {},
    {
      headers: {
        'Set-Cookie': setCookie(
          cookieHeader || '',
          'cookie_consent',
          JSON.stringify(cookieConsent),
          '325',
        ),
      },
    },
  )
}

export const loader: LoaderFunction = async ({ request }) => {
  const url = prepareRemixRequest(request)

  const cookieHeader = request.headers.get('Cookie')
  const cookieConsent = getCookieConsent(cookieHeader || '', 'cookie_consent')
  const PlanEntries = await getPlans()
  const HeaderNav = await getHeaderNav()
  const FooterNav = await getFooterNav()

  const totalJobSearchPromo = await ContentfulClientManager.getContentfulEntry(
    `7DwVyO4lv7H79d1e9zOjRj`,
    {
      include: 3,
      preview: true,
    },
  )

  const cookieConsentValue = getCookieConsentValue(cookieConsent)

  const contextValue = {
    ...preparePageContext(url),
    PlanEntries: PlanEntries,
    HeaderNav,
    FooterNav,
    totalJobSearchPromo,
    cookie_consent: cookieConsentValue,
    SENTRY: {
      DSN: process.env.CAREER_IO_SENTRY_DSN || '',
      ENVIRONMENT: (url.hostname === 'staging.career.io' ? 'staging' : process.env.NODE_ENV) || '',
      SENTRY_ENABLED_DEV: !!process.env.SENTRY_ENABLED_DEV,
    },
    ENV: {
      IMAGE_PROCESSOR_URL: process.env.IMAGE_PROCESSOR_URL || '',
    },
  }

  if (cookieConsent.length === 0) {
    const cookieConsent = ['EssentialCookies']
    return data(
      {
        ...contextValue,
      },
      {
        headers: {
          'Set-Cookie': setCookie(
            cookieHeader || '',
            'cookie_consent',
            JSON.stringify(cookieConsent),
            '325',
          ),
        },
      },
    )
  }

  return data(contextValue)
}

function App() {
  const { data } = useRouteLoaderData<CIOAppContext>('root') as CIOAppContext
  const { pathname } = useLocation()
  const [canonicalTag, setCanonicalTag] = useState(
    pathname ? `${data?.url?.origin}${pathname}` : data?.url?.origin,
  )

  useEffect(() => {
    TagManager.initialize({ gtmId: 'GTM-WNKWT46' })
  }, [])

  useEffect(() => {
    if (data?.url?.origin && pathname) setCanonicalTag(`${data.url.origin}${pathname}`)
  }, [data?.url?.origin, pathname])

  return (
    <QueryClientProvider client={queryClient}>
      <RootContext.Provider value={data}>
        <Document canonicalTag={canonicalTag}>
          <MainAppContextProvider>
            <Outlet />
            <UploadV2 />
          </MainAppContextProvider>
        </Document>
      </RootContext.Provider>
    </QueryClientProvider>
  )
}

export default withSentry(App)

export function ErrorBoundary() {
  const error = useRouteError()
  captureRemixErrorBoundaryError(error)

  const isRouteError = isRouteErrorResponse(error)
  const title = process.env.NODE_ENV === 'development' ? 'Career.io - App Error' : 'Career.io'

  return (
    <Document title={title}>
      {process.env.NODE_ENV == 'development' && (
        <>
          {isRouteError && (
            <>
              <h1>
                {error.status} {error.statusText}
              </h1>
              <p>{error.data}</p>
            </>
          )}
          {!isRouteError && error instanceof Error && (
            <>
              <h1>Error</h1>
              <p>{error.message}</p>
              <p>The stack trace is:</p>
              <pre>{error.stack}</pre>{' '}
            </>
          )}
        </>
      )}
      {process.env.NODE_ENV != 'development' && (
        <Layout headerBgColor={Colors.White}>
          <Layout.Content style={{ backgroundColor: Colors.White }}>
            <ErrorComponent isRouteError={isRouteError} />
          </Layout.Content>
        </Layout>
      )}
    </Document>
  )
}
